import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import {
  Icon,
  Text,
  Card,
  Stack,
  Button,
  AutoSavedSwitcherControl,
  Spinner,
  Tooltip,
  withTranslation,
} from '@piwikpro/ui-components';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { ModulesState } from '@piwikpro/modules-crate';
import { Users } from '@piwikpro/users-crate';
import { Modules } from '@piwikpro/modules-crate/Modules';
import { AdministrationState } from '../../../../../redux';

const mapStateToProps = (state: AdministrationState & ModulesState & { session: SessionState }) => {
  const { modules, isLoading, failed } = state.modules;

  return {
    loggedUserId: state.session.userId,
    user: state.users.details.user.details as NonNullable<AdministrationState['users']['details']['user']['details']>,
    isLoading,
    failed,
    modules,
  };
};

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  users: Users
  modulesApi: Modules
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const Privileges: React.FC<Props> = ({
  users,
  modulesApi,
  user,
  loggedUserId,
  isLoading,
  failed,
  modules,
  t,
}) => {
  useEffect(() => {
    const fetchUserModules = async () => {
      try {
        await modulesApi.fetchUserModules(user.id);
      // eslint-disable-next-line no-empty
      } catch {}
    };

    fetchUserModules();
  }, []);

  const handleModuleAccessSubmit = async (values, {
    setSubmitting,
    setFieldValue,
  }) => {
    try {
      await modulesApi.updateUserModuleAccess(
        user.id,
        values,
      );
      setSubmitting(false);
    } catch (err) {
      setFieldValue(Object.keys(values[0])[0], !Object.values(values[0])[0], false);
      setSubmitting(false);
    }
  };

  const handleRoleSubmit = async (values, {
    setSubmitting,
    setFieldValue,
  }) => {
    const userRole = values.role ? 'OWNER' : 'USER';

    try {
      await users.updateUser(user.id, { role: userRole });
      setSubmitting(false);
    } catch (err) {
      setFieldValue(Object.keys(values)[0], !Object.values(values)[0], false);
      setSubmitting(false);
    }
  };

  const renderModule = module => (
    <Stack.Item key={module.id}>
      <Stack verticalAlignment="center" spacing="narrow">
        <Stack.Item>
          <AutoSavedSwitcherControl
            id="user-profile-module-form"
            initialValue={module.attributes.access}
            name={module.id}
            onSubmit={(values, api) => handleModuleAccessSubmit([values], api)}
          />
        </Stack.Item>
        <Stack.Item>
          <Text weight={600} as="p" wrapType="noWrapAndCutText">{t(module.id)}</Text>
        </Stack.Item>
      </Stack>
    </Stack.Item>
  );

  const renderModules = () => {
    const allModulesEnabled = modules.every(module => module.attributes.access);

    if (isLoading) {
      return (
        <Card.Section subdued>
          <Spinner />
        </Card.Section>
      );
    }

    if (failed) {
      return (
        <Card.Section subdued>
          <Stack
            vertical
            horizontalAlignment="middle"
            verticalAlignment="center"
            spacing="extraWide"
          >
            <Stack.Item fill />
            <Stack.Item>
              <Stack
                vertical
                horizontalAlignment="middle"
              >
                <Stack.Item>
                  <Icon
                    color="red"
                    name="exclamation-mark-circle"
                    size="medium"
                  />
                </Stack.Item>
                <Stack.Item>
                  <Text color="Red" dataId="TextStyle">
                    {t('users.privileges.modules.failed')}
                  </Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item fill />
          </Stack>
        </Card.Section>
      );
    }

    return (
      <Card.Section subdued>
        <Stack vertical spacing="wide">
          <Stack.Item fill>
            <Stack verticalAlignment="top">
              <Stack.Item fill>
                <Stack vertical spacing="narrow">
                  <Stack.Item>
                    <Text as="h3" size={16} weight={600} wrapType="noWrapAndCutText" dataId="Label.large">{t('users.privileges.modulesAccess.title')}</Text>
                  </Stack.Item>
                  <Stack.Item previousSpacing="extraNarrow">
                    <Text color="InkLighter" as="p">{t('users.privileges.modulesAccess.description')}</Text>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
              <Stack.Item>
                <Button
                  appearance="simple"
                  size="large"
                  text={allModulesEnabled
                    ? t('users.privileges.modulesAccess.removeAll')
                    : t('users.privileges.modulesAccess.giveAll')
                  }
                  onClick={() => handleModuleAccessSubmit(
                    allModulesEnabled
                      ? modules.map(module => ({ [module.id]: false }))
                      : modules.map(module => ({ [module.id]: true })),
                    {
                      setSubmitting: () => {},
                      setFieldValue: () => {},
                    },
                  )}
                />
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item fill previousSpacing="default">
            <Stack wrap verticalAlignment="center" spacing="wide">
              {modules.map(renderModule)}
            </Stack>
          </Stack.Item>
        </Stack>
      </Card.Section>
    );
  };

  const isLoggedUserAndOwner = user.id === loggedUserId && user.attributes.role === 'OWNER';
  const roleSwitcher = (disabled: boolean) => (
    <AutoSavedSwitcherControl
      id="user-profile-role-form"
      dataId="user-profile-role-switcher"
      initialValue={user.attributes.role !== 'USER'}
      name="role"
      disabled={disabled}
      onSubmit={handleRoleSubmit}
    />
  );

  return (
    <>
      <Card.Section>
        <Stack wrap verticalAlignment="top" spacing="wide">
          <Stack.Item>
            {isLoggedUserAndOwner ? (
              <Tooltip content={t('users.privileges.forbidden.changeOwnOwnerRole.info')}>
                <div>
                  {roleSwitcher(true)}
                </div>
              </Tooltip>
            ) : roleSwitcher(false)}
          </Stack.Item>
          <Stack.Item fill>
            <Stack vertical spacing="narrow">
              <Stack.Item>
                <Text as="h2" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">{t('users.privileges.type.title')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Text color="InkLighter" as="p">{t('users.privileges.ownerAccess.description')}</Text>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Card.Section>
      {user.attributes.role === 'USER' && renderModules()}
    </>
  );
};

export default connector(withInject<InjectedProps>({
  users: 'UsersCrate.users',
  modulesApi: 'ModulesCrate.modules',
})(
  withTranslation('administration')(errorBoundary(Privileges)),
));
